/** 解析 IntersectionObserver rootMargin，如 '240px' 或 '10px 20px' */
export function parseRootMargin(margin) {
  const parts = String(margin || '0').trim().split(/\s+/).filter(Boolean);
  const values = parts.map((part) => {
    const num = parseFloat(part);
    return Number.isFinite(num) ? num : 0;
  });
  if (values.length === 0) {
    return { top: 0, right: 0, bottom: 0, left: 0 };
  }
  if (values.length === 1) {
    return { top: values[0], right: values[0], bottom: values[0], left: values[0] };
  }
  if (values.length === 2) {
    return { top: values[0], right: values[1], bottom: values[0], left: values[1] };
  }
  if (values.length === 3) {
    return { top: values[0], right: values[1], bottom: values[2], left: values[1] };
  }
  return {
    top: values[0],
    right: values[1],
    bottom: values[2],
    left: values[3],
  };
}

function isScrollable(el) {
  if (!el) return false;
  const style = getComputedStyle(el);
  return ['auto', 'scroll', 'overlay'].includes(style.overflowY)
    || ['auto', 'scroll', 'overlay'].includes(style.overflowX);
}

/** 从节点向上查找最近的可滚动祖先 */
export function findScrollableParent(node) {
  let current = node?.parentElement;
  while (current && current !== document.documentElement) {
    if (isScrollable(current)) return current;
    current = current.parentElement;
  }
  return null;
}

export function resolveScrollRoot(selector) {
  if (!selector) return null;
  return document.querySelector(selector);
}

/**
 * 解析实际用于懒加载检测的滚动容器。
 * 优先使用 selector 对应且可滚动的节点；否则在 selector 子树或 node 祖先中查找。
 */
export function getEffectiveScrollRoot(node, selector) {
  const fromSelector = selector ? document.querySelector(selector) : null;

  if (fromSelector && isScrollable(fromSelector)) {
    return fromSelector;
  }

  if (fromSelector) {
    const innerScroll = fromSelector.querySelector(
      '.admin-official-page__body, .funding-workbench__main-body',
    );
    if (innerScroll && isScrollable(innerScroll)) {
      return innerScroll;
    }
  }

  const fromNode = findScrollableParent(node);
  if (fromNode) return fromNode;

  return fromSelector;
}

/** 收集需要监听的滚动容器（主 root + 常见 Admin 回退） */
export function collectScrollTargets(node, selector) {
  const targets = new Set();
  const primary = getEffectiveScrollRoot(node, selector);
  if (primary) targets.add(primary);

  if (selector) {
    const configured = document.querySelector(selector);
    if (configured) targets.add(configured);
  }

  document.querySelectorAll('.admin-official-page__body, .admin-shell__main').forEach((el) => {
    if (isScrollable(el)) targets.add(el);
  });

  document.querySelectorAll('.wf-tasks-page.sourcing-hub .wf-tasks-table-wrap').forEach((el) => {
    if (isScrollable(el)) targets.add(el);
  });

  const ancestor = findScrollableParent(node);
  if (ancestor) targets.add(ancestor);

  return [...targets];
}

/** 手动判断节点是否在 scrollRoot（或视口）+ rootMargin 范围内 */
export function isNodeInLazyView(node, root, rootMargin) {
  if (!node) return false;
  const rect = node.getBoundingClientRect();
  if (rect.width <= 0 && rect.height <= 0) return false;

  const margin = parseRootMargin(rootMargin);
  if (root) {
    const rootRect = root.getBoundingClientRect();
    return (
      rect.bottom >= rootRect.top - margin.top
      && rect.top <= rootRect.bottom + margin.bottom
      && rect.right >= rootRect.left - margin.left
      && rect.left <= rootRect.right + margin.right
    );
  }

  const viewW = window.innerWidth || document.documentElement.clientWidth;
  const viewH = window.innerHeight || document.documentElement.clientHeight;
  return (
    rect.bottom >= -margin.top
    && rect.top <= viewH + margin.bottom
    && rect.right >= -margin.left
    && rect.left <= viewW + margin.right
  );
}

/** 布局稳定后唤醒懒加载检测（列表数据刷新后调用） */
export function nudgeLazyLoadCheck(scrollRootSelector) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (scrollRootSelector) {
        const root = resolveScrollRoot(scrollRootSelector);
        if (root) {
          root.dispatchEvent(new Event('scroll'));
        }
        if (scrollRootSelector.includes('admin-shell')) {
          document.querySelectorAll('.admin-official-page__body').forEach((el) => {
            if (isScrollable(el)) {
              el.dispatchEvent(new Event('scroll'));
            }
          });
        }
      }
      window.dispatchEvent(new Event('resize'));
    });
  });
}

/** Admin 官方素材页：同时唤醒 shell 与内容区滚动检测 */
export function nudgeAdminLazyLoadCheck() {
  nudgeLazyLoadCheck('.admin-shell__main');
  nudgeLazyLoadCheck('.admin-official-page__body');
}
